import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { useGetLabsQuery, useUpdateLabMutation } from '@/api/api-hooks';
import type { Lab, LabDisableConfirmation } from '@/api/types/labs';
import { RequireEntitlements } from '@/components/Auth/RequireEntitlements';
import { SettingsPageLayout } from '@/components/Settings/SettingsPageLayout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { showErrorToast } from '@/errors/show-error-toast';
import { useUser } from '@/hooks/useUser';

export const Route = createFileRoute('/$organizationName/settings/labs')({
  component: SecuredLabsPage,
});

function SecuredLabsPage() {
  return (
    <RequireEntitlements admins={['isSuperAdmin']}>
      <LabsPage />
    </RequireEntitlements>
  );
}

interface PendingDisable {
  lab: Lab;
  confirmation: LabDisableConfirmation;
}

function LabsPage() {
  const { organizationDisplayName } = useUser();
  const { data, isLoading, isError } = useGetLabsQuery();
  const [pendingDisable, setPendingDisable] = useState<PendingDisable | null>(null);
  const { mutateAsync: updateLab, isPending } = useUpdateLabMutation();

  useEffect(() => {
    document.title = `${organizationDisplayName || 'Driver'} - Labs`;
  }, [organizationDisplayName]);

  async function handleToggle(lab: Lab) {
    try {
      if (lab.enabled && lab.destructive_disable) {
        const result = await updateLab({ key: lab.key, enabled: false });
        if (result?.confirmation_required) {
          setPendingDisable({ lab, confirmation: result });
          return;
        }
      } else {
        await updateLab({ key: lab.key, enabled: !lab.enabled });
      }
    } catch (error) {
      showErrorToast(error, 'There was a problem updating the lab.');
    }
  }

  async function handleConfirmDisable() {
    if (!pendingDisable) return;
    try {
      await updateLab({ key: pendingDisable.lab.key, enabled: false, confirmed: true });
      setPendingDisable(null);
    } catch (error) {
      showErrorToast(error, 'There was a problem updating the lab.');
    }
  }

  return (
    <SettingsPageLayout title="Labs" subTitle="Enable experimental features for your organization">
      <div className="space-y-4">
        {isLoading ? (
          <>
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </>
        ) : isError ? (
          <div className="text-destructive text-sm">Failed to load labs. Please try again later.</div>
        ) : (
          data?.labs.map((lab) => (
            <div key={lab.key} className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <div className="text-sm font-medium">{lab.name}</div>
                {lab.description && <div className="text-muted-foreground text-sm">{lab.description}</div>}
              </div>
              <Switch checked={lab.enabled} disabled={isPending} onCheckedChange={() => handleToggle(lab)} />
            </div>
          ))
        )}
        {data?.labs.length === 0 && !isLoading && <div className="text-muted-foreground">No labs available</div>}
      </div>

      <AlertDialog open={pendingDisable !== null} onOpenChange={(open) => !open && setPendingDisable(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable {pendingDisable?.lab.name}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>{pendingDisable?.confirmation.message}</p>
                <p>
                  This will permanently delete {pendingDisable?.confirmation.affected_items.installations} provider
                  connection(s).
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleConfirmDisable} disabled={isPending}>
              Disable and Delete Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsPageLayout>
  );
}
